import { useEffect, useState } from 'react';
import { Button } from '@sabay/ui-kit';
import {
  getContentConfigs,
  type ConsumerContentConfig,
} from '@/lib/api';
import { useLocale } from '@/lib/i18n';

/**
 * Koom GO 首页「活动栏（拼团等）」黄色推广位。
 *
 * 数据来源：`getContentConfigs({ placement: 'HOME_ACTIVITY', cityCode })` —
 * SPEC-032 (Koom GO mini-app redesign) 新增的 placement，由运营在 admin-web
 * 「运营位配置 → 新建首页活动栏」录入。
 *
 * 视觉规格（DESIGN.md §Components > Activity Banner）：
 *   - 形状：rounded-2xl (20px)
 *   - 背景：solid `brand.primary.500` `#FFC400`
 *   - 文字：`neutral.900` `#131009` semibold（AA 10.3:1 ✓）
 *   - 内边距：垂直 18px / 水平 20px
 *   - 宽度：占满页面 padding 内
 *
 * 当 cityCode 下没有任何 active+within-time-window 的 HOME_ACTIVITY 行时，
 * 整段返回 `null`（不渲染 placeholder / sample 文本，遵守 CLAUDE.md「No Mock Data」原则）。
 */
export function ActivityBanner({ cityCode }: { cityCode: string }): React.ReactElement | null {
  const locale = useLocale();
  const [items, setItems] = useState<ConsumerContentConfig[] | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    setItems(null);
    getContentConfigs({ placement: 'HOME_ACTIVITY', cityCode }, ac.signal)
      .then((res) => setItems(res.items))
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        // 真实查询失败时不展示 banner（与「无数据」表现一致），同时记录日志便于排查。
        // 不展示 fallback 文案以避免误导成"暂时没活动"。
        console.warn('[home.activity-banner] fetch failed', { cityCode }, err);
        setItems([]);
      });
    return () => ac.abort();
  }, [cityCode]);

  if (!items || items.length === 0) return null;

  // 当前迭代只展示首条 active HOME_ACTIVITY；多条由运营通过 orderIndex 排序，
  // 第二条起将作为后续 carousel 兼容预留。
  const item = items[0];
  if (!item) return null;
  const title = pickTitle(item, locale);
  const subtitle = pickSubtitle(item, locale);

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => openExternalLink(item.targetUrl)}
      className="
        block w-full text-left
        rounded-2xl
        bg-brand-primary-500
        px-5 py-4
        text-neutral-900
        transition active:scale-[0.985]
        shadow-sm
      "
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold leading-snug truncate">{title}</p>
          {subtitle ? (
            <p className="text-xs font-medium text-neutral-900/80 leading-snug truncate mt-0.5">
              {subtitle}
            </p>
          ) : null}
        </div>
        <ChevronRightGlyph />
      </div>
    </Button>
  );
}

function pickTitle(c: ConsumerContentConfig, locale: string): string {
  if (locale === 'km-KH') return c.titleKm;
  if (locale === 'en') return c.titleEn;
  return c.titleZh;
}

function pickSubtitle(c: ConsumerContentConfig, locale: string): string | null {
  if (locale === 'km-KH') return c.subtitleKm;
  if (locale === 'en') return c.subtitleEn;
  return c.subtitleZh;
}

function openExternalLink(url: string): void {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const openLink = (tg as unknown as { openLink?: (u: string) => void } | undefined)?.openLink;
  if (typeof openLink === 'function') {
    openLink.call(tg, url);
    return;
  }
  window.location.href = url;
}

function ChevronRightGlyph(): React.ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <path d="M7 4l5 5-5 5" />
    </svg>
  );
}
